import SimpleModal from "app/components/Modals/SimpleModal";
import {
  selectAllCompanyUsersById,
  updateUserById,
} from "app/containers/Global/slice";
import { useAppDispatch, useAppSelector } from "utils/redux/hooks";
import TextInput from "app/storybookComponents/Forms/TextInput";
import { useEffect, useMemo, useState } from "react";
import SimpleModalFooter from "app/components/Modals/SimpleModalFooter";
import {
  checkNewEmail,
  checkUserEmail,
  selectCheckedEditedEmails,
  selectCheckUserEmailStatus,
  selectCheckNewEmailStatus,
  selectNewCheckedEmails,
} from "app/containers/AdminConsole/slice";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  userAccountId: number | null;
}

const EditUserEmail = ({ isOpen, onClose, userAccountId }: Readonly<Props>) => {
  const dispatch = useAppDispatch();
  const usersById = useAppSelector(selectAllCompanyUsersById);
  const checkExistingEmailStatus = useAppSelector(selectCheckUserEmailStatus);
  const checkNewEmailStatus = useAppSelector(selectCheckNewEmailStatus);
  const [emailAddress, setEmailAddress] = useState("");
  const checkedEmails = useAppSelector(selectNewCheckedEmails);
  const checkedEditedEmails = useAppSelector(selectCheckedEditedEmails);
  const [isEmailFieldInFocus, setIsEmailFieldInFocus] = useState(false);

  const checkedEmail = useMemo(() => {
    if (!emailAddress) return null;
    return userAccountId
      ? checkedEditedEmails[emailAddress]
      : checkedEmails[emailAddress];
  }, [checkedEmails, emailAddress, userAccountId, checkedEditedEmails]);
  const isInvalidEmail = checkedEmail?.isValid === false;

  useEffect(() => {
    if (userAccountId && usersById[userAccountId]) {
      setEmailAddress(usersById[userAccountId].emailAddress ?? "");
    }
  }, [userAccountId, usersById]);

  const onEmailUnFocus = () => {
    setIsEmailFieldInFocus(false);
    // If the email is the same as the user's email, we don't need to check
    if (
      userAccountId &&
      usersById[userAccountId]?.emailAddress === emailAddress
    ) {
      return;
    }

    if (userAccountId) {
      dispatch(checkUserEmail({ emailAddress, userAccountId }));
    } else {
      dispatch(checkNewEmail(emailAddress));
    }
  };

  const getEmailInputClass = () => {
    let className = "column";

    if (isInvalidEmail) {
      className += " danger";
    }
    return className;
  };

  return (
    <SimpleModal show={isOpen} onHide={onClose} title="Edit Email">
      <div className="column-gap-20px">
        <TextInput
          controlId="emailAddress"
          inputText={emailAddress}
          onTextChange={setEmailAddress}
          inputLabel="Email Address"
          className={getEmailInputClass()}
          onUnFocus={onEmailUnFocus}
          onFocus={() => setIsEmailFieldInFocus(true)}
        />
        {isInvalidEmail ? (
          <div className="warning-banner red">
            <p>
              {checkedEmail?.error ??
                "This email is invalid please enter a new email."}
            </p>
          </div>
        ) : null}
        <SimpleModalFooter
          onPrimaryButtonClick={async () => {
            if (!userAccountId) {
              return;
            }
            await dispatch(
              updateUserById({
                userAccountId,
                payload: { emailAddress },
              })
            );
            onClose();
          }}
          primaryButtonText="Save"
          isPrimaryButtonDisabled={
            emailAddress === "" ||
            isInvalidEmail ||
            isEmailFieldInFocus ||
            checkExistingEmailStatus === "loading" ||
            checkNewEmailStatus === "loading"
          }
        />
      </div>
    </SimpleModal>
  );
};

export default EditUserEmail;
